import { TimeHTMLAttributes } from "react"
import useIntl from "src/hooks/use-intl"
import useLiveConcertState from "src/hooks/use-live-concert-state"
import useTranslate from "src/hooks/use-translate"
import { DateFormatStyle } from "src/utilities/intl-helpers"

import { theme, TI } from "../../utilities/theme"
import { LiveConcertExtract } from "../Sliders/Slider"

import { DGText } from "./DGText"

type LiveConcertTimeProps = {
  liveConcert: Pick<
    LiveConcertExtract,
    "endTime" | "startTime" | "streamStartTime" | "reruns" | "typeDisplayName"
  >
}

/**
 * Renders the live concert time based on the current time and the live concert start time.
 * When the live concert is in the future, it renders the start time.
 * When the live concert is live, it renders "Live now".
 * When the live concert is in the past, it renders "Available soon in the archive".
 */
export function LiveConcertTime({ liveConcert }: LiveConcertTimeProps) {
  const t = useTranslate()
  const { status, startTime } = useLiveConcertState(liveConcert)

  // TODO remove repetitions
  if (status === "past")
    return (
      <DGText
        tx={TI.Subheadline05tv}
        sx={{
          color: theme.palette.tertiary.accentYellow,
          textTransform: "uppercase",
          textAlign: "center",
        }}
      >
        {t("live_concert__status_label_past")}
      </DGText>
    )

  if (!startTime) return null

  if (status === "live" || status === "pre-live") {
    return (
      <DGText
        tx={TI.Subheadline05tv}
        sx={{
          color: theme.palette.tertiary.accentYellow,
          textTransform: "uppercase",
          textAlign: "center",
        }}
      >
        {t("live_concert__live_now")}
      </DGText>
    )
  }

  return (
    <DGText
      tx={TI.Subheadline05tv}
      sx={{
        color: theme.palette.tertiary.accentYellow,
        textAlign: "center",
        textTransform: "uppercase",
      }}
    >
      {liveConcert.typeDisplayName}&nbsp;&ndash;&nbsp;
      <Time value={startTime} style="shortTime" />
    </DGText>
  )
}

type TimeProps = TimeHTMLAttributes<HTMLTimeElement> & {
  value: string | number | Date // Date primitive
  style: DateFormatStyle
}

export const Time = ({ value, style, ...rest }: TimeProps) => {
  const { dateFormat } = useIntl()
  const date = value instanceof Date ? value : new Date(value)

  return (
    <time
      dateTime={date.toISOString()}
      className="lining-nums"
      {...rest}
      suppressHydrationWarning
    >
      {dateFormat(date, style)}
    </time>
  )
}
